import { useState, useRef, useEffect, useCallback } from "react";
import { useChat } from "hooks/useChat";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import Message from "types/Message";

import React from "react";
import { toCamelCase, uploadFile } from "apiClient";
import { GripHorizontal, Loader2, Paperclip, Send, X } from "lucide-react";
import { MessageBalloon } from "./MessageBalloon";
import { useCondos } from "context/CondosContext";
import { useManagers } from "context/ManagersContext";
import {
  ELORIA_PHONE,
  ELORIA_WHATSAPP_NUMBER,
} from "constants/eloriaContact";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { useAuth } from "context/AuthContext";
import { useAssistantIdentity } from "hooks/useAssistantIdentity";
import { formatConnectedWappPhone } from "components/PropertyManagerWhatsappCard";
import {
  ensureMessageListKey,
  isOptimisticMessage,
  upsertWebsocketMessage,
} from "utils/message";
import { formatChatDateSeparator } from "utils/dateFormat";
import { subscribeContactChatSocket } from "utils/contactChatSocket";

interface ChatProps {
  contactId: number;
  contactName?: string;
  condoMode?: "simulated" | "live";
  messageId?: string | null;
  lastReadAt?: string | null;
  /** Fired when the backend reports a specific list should refresh. */
  onEntitiesMaybeChanged?: (entity: EntityListScope) => void;
}

export type EntityListScope =
  | "condos"
  | "residents"
  | "workers"
  | "providers"
  | "board_members";

const COMPOSER_MIN_HEIGHT = 40;
const COMPOSER_MAX_HEIGHT = 128;

export function Chat({
  contactId,
  contactName,
  condoMode,
  messageId,
  lastReadAt,
  onEntitiesMaybeChanged,
}: ChatProps) {
  const [page, setPage] = useState(1);
  const [allMessages, setAllMessages] = useState<Message[]>([]);
  const [composerHeight, setComposerHeight] = useState(COMPOSER_MIN_HEIGHT);
  const composerResizeRef = useRef<{
    startY: number;
    startHeight: number;
  } | null>(null);
  const previousContactIdRef = useRef<number | null>(null);
  const initialLastReadAtRef = useRef<string | null>(null);

  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const hasScrolledToMessage = useRef(false);
  const loadedPages = useRef<Set<number>>(new Set());
  const socketContactIdRef = useRef<number | null>(null);
  const previousScrollHeight = useRef<number>(0);
  const pageRef = useRef(1);
  const nextRef = useRef<string | null>(null);
  const loadingRef = useRef(false);
  const loadMoreTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { selectedCondoId, selectedCondoMode } = useCondos();
  const { managers } = useManagers();
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const { assistantName, connectedWappPhone } = useAssistantIdentity();

  const wappNumberDigits =
    connectedWappPhone?.replace(/\D/g, "") || ELORIA_WHATSAPP_NUMBER;
  const wappPhoneDisplay =
    formatConnectedWappPhone(connectedWappPhone) || ELORIA_PHONE;
  const wappUrl = `https://wa.me/${wappNumberDigits}`;

  const selectedManager = managers.find(
    (manager) => manager.contact.id === contactId
  );
  const isManagerContact = selectedManager != null;
  const selectedManagerHasPhone = Boolean(selectedManager?.contact.phone);
  const onEntitiesMaybeChangedRef = useRef(onEntitiesMaybeChanged);
  onEntitiesMaybeChangedRef.current = onEntitiesMaybeChanged;

  // Reset page to 1 and store initial lastReadAt when contactId changes
  useEffect(() => {
    if (previousContactIdRef.current !== contactId) {
      previousContactIdRef.current = contactId;
      initialLastReadAtRef.current = lastReadAt || null;
      setPage(1);
      pageRef.current = 1;
      setComposerHeight(COMPOSER_MIN_HEIGHT);
    }
  }, [contactId, lastReadAt]);

  const { messages, loading, error, next, sendMessage } = useChat(
    selectedCondoId,
    contactId,
    page
  );

  // Use the initial lastReadAt for separator logic (so it doesn't change when context updates)
  const effectiveLastReadAt = initialLastReadAtRef.current;

  // Keep refs up to date
  useEffect(() => {
    pageRef.current = page;
    nextRef.current = next;
    loadingRef.current = loading;
  }, [page, next, loading]);

  // Use infinite scroll hook for loading older messages
  const loadMoreMessages = () => {
    if (!loadingRef.current && nextRef.current) {
      const nextPage = pageRef.current + 1;
      if (!loadedPages.current.has(nextPage)) {
        setPage(nextPage);
      }
    }
  };

  const scrollContainerRef = useInfiniteScroll({
    hasMore: !!next,
    loading,
    onLoadMore: loadMoreMessages,
  });

  useEffect(() => {
    // Track which contact this subscription is for
    const currentContactId = contactId;
    socketContactIdRef.current = currentContactId;

    const unsubscribe = subscribeContactChatSocket(contactId, (event) => {
      // Only process messages if this subscription is still for the current contact
      if (socketContactIdRef.current !== currentContactId) {
        return;
      }

      const raw = JSON.parse(event.data);
      if (raw?.type === "checklist_changed") {
        return;
      }
      if (raw?.type === "entities_changed" && raw?.entity) {
        const entity = raw.entity as string;
        if (
          entity === "condos" ||
          entity === "residents" ||
          entity === "workers" ||
          entity === "providers" ||
          entity === "board_members"
        ) {
          onEntitiesMaybeChangedRef.current?.(entity);
        }
        return;
      }

      const message = ensureMessageListKey(toCamelCase(raw) as Message);
      setAllMessages((prev) => {
        // Remove optimistic messages only when the real message replaces them fully
        // (e.g. do not drop image+text optimistic when websocket sends text-only first)
        const filtered = prev.filter((m) => {
          if (!isOptimisticMessage(m)) return true;
          if (m.content !== message.content) return true;
          if (m.fileUrl && !message.fileUrl) return true;
          return false;
        });

        return upsertWebsocketMessage(filtered, message);
      });
    });

    return () => {
      unsubscribe();
      socketContactIdRef.current = null;
    };
  }, [contactId]);

  useEffect(() => {
    // Reset everything when contactId or messageId changes
    setAllMessages([]);
    setPage(1);
    loadedPages.current = new Set();
    hasScrolledToMessage.current = false;
    messageRefs.current.clear();
    previousScrollHeight.current = 0;
    pageRef.current = 1;
    nextRef.current = null;
    loadingRef.current = false;
    previousContactIdRef.current = contactId;
  }, [contactId, messageId]);

  useEffect(() => {
    // Process messages if:
    // 1. We haven't loaded this page yet, OR
    // 2. We have messages but allMessages is empty (race condition fix)
    const shouldProcess =
      !loadedPages.current.has(page) ||
      (messages.length > 0 && allMessages.length === 0 && page === 1);

    if (shouldProcess) {
      if (messages && messages.length > 0) {
        // Store scroll height and position before adding new messages (for scroll position restoration)
        const container = scrollContainerRef.current;
        let scrollHeightBefore = 0;
        let scrollTopBefore = 0;

        if (page > 1 && container) {
          scrollHeightBefore = container.scrollHeight;
          scrollTopBefore = container.scrollTop;
          previousScrollHeight.current = scrollHeightBefore;
        }

        setAllMessages((prev) => {
          const existingKeys = new Set(prev.map((m) => m.listKey));
          const newMessages = messages.filter((m) => !existingKeys.has(m.listKey));

          if (newMessages.length > 0) {
            loadedPages.current.add(page);
          }

          return [...prev, ...newMessages];
        });

        // Restore scroll position after DOM updates
        if (page > 1 && container && scrollHeightBefore > 0) {
          // Use requestAnimationFrame to wait for DOM to update
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (container) {
                const newScrollHeight = container.scrollHeight;
                const heightDiff = newScrollHeight - scrollHeightBefore;
                if (heightDiff > 0) {
                  // Adjust scroll position to maintain visual position
                  container.scrollTop = scrollTopBefore + heightDiff;
                }
                previousScrollHeight.current = 0;
              }
            });
          });
        }
      } else if (!loading && messages && messages.length === 0) {
        // Only mark as loaded if loading is complete AND we have an empty array
        // This prevents marking as loaded while still loading
        loadedPages.current.add(page);
      }
    }
  }, [messages, page, loading, allMessages.length, scrollContainerRef]);

  const targetMessageId = messageId ?? null;
  const allMessagesRef = useRef(allMessages);
  allMessagesRef.current = allMessages;

  const messageMatchesTarget = useCallback(
    (msg: Message) => {
      if (!targetMessageId) return false;
      return (
        msg.id.toString() === targetMessageId || msg.listKey === targetMessageId
      );
    },
    [targetMessageId]
  );

  // Scroll to target message when it's loaded (prioritize this over loading more pages)
  useEffect(() => {
    if (
      targetMessageId &&
      allMessages.length > 0 &&
      !hasScrolledToMessage.current &&
      !loading
    ) {
      const targetMessage = allMessages.find(messageMatchesTarget);
      if (targetMessage) {
        const scrollToMessage = () => {
          const messageElement = messageRefs.current.get(targetMessage.listKey);
          if (messageElement) {
            messageElement.scrollIntoView({
              behavior: "auto",
              block: "center",
            });
            hasScrolledToMessage.current = true;
          } else {
            // Element not in refs yet, try again after another frame
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const retryElement = messageRefs.current.get(
                  targetMessage.listKey
                );
                if (retryElement) {
                  retryElement.scrollIntoView({
                    behavior: "auto",
                    block: "center",
                  });
                  hasScrolledToMessage.current = true;
                }
              });
            });
          }
        };

        // Use multiple RAFs to ensure DOM is fully updated
        requestAnimationFrame(() => {
          requestAnimationFrame(scrollToMessage);
        });
      }
    }
  }, [allMessages, targetMessageId, loading, messageMatchesTarget]);

  // Load more pages if target message is not found yet (only after we've scrolled or confirmed it's not on current page)
  useEffect(() => {
    // Clear any existing timeout
    if (loadMoreTimeoutRef.current) {
      clearTimeout(loadMoreTimeoutRef.current);
      loadMoreTimeoutRef.current = null;
    }

    // Don't do anything if we've already scrolled to the message
    if (hasScrolledToMessage.current) {
      return;
    }

    if (targetMessageId && !loading && next) {
      const targetMessage = allMessages.find(messageMatchesTarget);

      // If message is found, don't load more pages
      if (targetMessage) {
        return;
      }

      // Only try to load more pages if:
      // 1. Message not found in allMessages
      // 2. Current page is loaded (messages have been processed)
      // 3. We have messages in allMessages (ensures messages from current page have been processed)
      // 4. We're on page 1 and have processed all messages from page 1
      if (
        !targetMessage &&
        loadedPages.current.has(page) &&
        allMessages.length > 0
      ) {
        // For page 1, wait a bit longer to ensure all messages are processed
        // For other pages, we can check immediately
        const delay = page === 1 ? 800 : 300;

        loadMoreTimeoutRef.current = setTimeout(() => {
          // Re-check with latest messages (allMessages in closure may be stale)
          const recheckMessage = allMessagesRef.current.find(messageMatchesTarget);
          if (!recheckMessage && !hasScrolledToMessage.current) {
            // Message still not found, load next page
            const nextPage = page + 1;
            if (!loadedPages.current.has(nextPage)) {
              setPage(nextPage);
            }
          }

          loadMoreTimeoutRef.current = null;
        }, delay);
      }
    }

    return () => {
      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current);
        loadMoreTimeoutRef.current = null;
      }
    };
  }, [targetMessageId, allMessages, loading, next, page, messageMatchesTarget]);

  // Scroll to bottom on first load only (if no message-id)
  useEffect(() => {
    if (
      page === 1 &&
      allMessages.length > 0 &&
      !targetMessageId &&
      scrollContainerRef.current
    ) {
      // Scroll to bottom (which is the end of the reversed list)
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, allMessages, targetMessageId]);

  const [messageToSend, setMessageToSend] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  const handleComposerResizePointerDown = (
    event: React.PointerEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    composerResizeRef.current = {
      startY: event.clientY,
      startHeight: composerHeight,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleComposerResizePointerMove = (
    event: React.PointerEvent<HTMLButtonElement>
  ) => {
    if (!composerResizeRef.current) return;

    const deltaY = composerResizeRef.current.startY - event.clientY;
    const nextHeight = Math.min(
      COMPOSER_MAX_HEIGHT,
      Math.max(
        COMPOSER_MIN_HEIGHT,
        composerResizeRef.current.startHeight + deltaY
      )
    );
    setComposerHeight(nextHeight);
  };

  const handleComposerResizePointerEnd = (
    event: React.PointerEvent<HTMLButtonElement>
  ) => {
    composerResizeRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setFileError(
          locale === "es"
            ? "El archivo es demasiado grande (máx 50MB)"
            : "The file is too large (max 50MB)"
        );
        setSelectedFile(null);
      } else {
        setFileError(null);
        setSelectedFile(file);
      }
    }
    // Reset input value so same file can be selected again if removed
    e.target.value = "";
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError(null);
  };

  const handleSend = async () => {
    if (uploading) return;

    const content = messageToSend.trim();
    if (!content && !selectedFile) return;

    let fileUrl: string | null = null;
    if (selectedFile) {
      setUploading(true);
      try {
        fileUrl = await uploadFile(selectedFile);
      } catch (err) {
        setFileError(
          locale === "es" ? "Error subiendo el archivo" : "Error uploading file"
        );
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    // Messages from Administracion contacts should always be green (user type)
    // In simulated mode, all messages are user type
    // In live mode, only non-manager contacts use manager type
    const messageType =
      selectedCondoMode === "simulated" || isManagerContact
        ? "user"
        : "manager";

    const optimisticId = -Date.now();
    const optimisticMessage: Message = {
      id: optimisticId,
      listKey: String(optimisticId),
      type: messageType,
      content,
      fileUrl,
      fileName: selectedFile ? selectedFile.name : null,
      sentAt: new Date().toISOString(),
      channel:
        isManagerContact && !selectedManagerHasPhone ? "panel" : "fake",
      subject: null,
    };

    setAllMessages((prev) => [optimisticMessage, ...prev]);
    setMessageToSend("");
    setSelectedFile(null);
    setFileError(null);
    await sendMessage(content, selectedCondoId, fileUrl);
  };

  const managerSendsOwnMessages =
    isManagerContact && selectedCondoId == null;

  const managerLiveChat =
    isManagerContact &&
    (selectedCondoMode === "live" || condoMode === "live") &&
    selectedManagerHasPhone;

  const usesContactMessageComposer =
    (condoMode === "simulated" || managerSendsOwnMessages) &&
    Boolean(contactName);

  const composerPlaceholder = usesContactMessageComposer
    ? locale === "es"
      ? `Enviar un mensaje como ${contactName}...`
      : `Send a message as ${contactName}...`
    : managerSendsOwnMessages
      ? locale === "es"
        ? "Enviar un mensaje..."
        : "Send a message..."
      : locale === "es"
        ? `Enviar una instrucción a ${assistantName}...`
        : `Send an instruction to ${assistantName}...`;

  const emptyStateSubtitle = (() => {
    if (managerLiveChat) {
      return locale === "es"
        ? `En modo en vivo, escribime por WhatsApp al ${wappPhoneDisplay} para iniciar la conversación.`
        : `In live mode, message me on WhatsApp at ${wappPhoneDisplay} to start the conversation.`;
    }
    if (usesContactMessageComposer) {
      return locale === "es"
        ? `Enviá el primer mensaje simulado como ${contactName}.`
        : `Send the first simulated message as ${contactName}.`;
    }
    if (managerSendsOwnMessages) {
      return locale === "es"
        ? "Enviá el primer mensaje usando el campo de abajo."
        : "Send the first message using the field below.";
    }
    return locale === "es"
      ? `Enviá la primera instrucción a ${assistantName} usando el campo de abajo.`
      : `Send the first instruction to ${assistantName} using the field below.`;
  })();

  const canSendMessage =
    !uploading &&
    (Boolean(messageToSend.trim()) || Boolean(selectedFile));

  const whatsAppFooter = (
    <div className="shrink-0 rounded-lg border border-blue-200 bg-blue-50 p-4 text-center space-y-3">
      <p className="text-sm text-blue-700">
        {locale === "es"
          ? "Para mandarme mensajes escríbeme por WhatsApp al "
          : "To message me, write to WhatsApp at "}
        <a
          href={wappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold underline underline-offset-2 hover:text-blue-900"
        >
          {wappPhoneDisplay}
        </a>
      </p>
      <a
        href={wappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
      >
        <WhatsAppIcon className="h-4 w-4" />
        {locale === "es"
          ? "Enviar mensaje por WhatsApp"
          : "Send a message on WhatsApp"}
      </a>
    </div>
  );

  return (
    <div className="chat-container flex h-full min-h-0 w-full min-w-0 flex-1 flex-col gap-2 lg:gap-3">
      {/* Messages list */}
      <div
        ref={scrollContainerRef}
        className="chat-messages-pane min-h-0 flex-1 overflow-y-auto space-y-3 px-2 py-2 lg:px-3 lg:py-3 w-full"
      >
        {error && (
          <div className="text-red-500 text-xs lg:text-sm">{error}</div>
        )}
        {loading && allMessages.length === 0 && (
          <div className="flex justify-center py-12 text-sm text-gray-500">
            {locale === "es" ? "Cargando mensajes…" : "Loading messages…"}
          </div>
        )}
        {!loading && allMessages.length === 0 && !error && (
          <div className="flex flex-col items-center py-8 px-6 text-center">
            <p className="text-sm font-medium text-gray-700">
              {locale === "es"
                ? "Todavía no hay mensajes"
                : "No messages yet"}
            </p>
            <p className="mt-2 max-w-sm text-xs text-gray-500">
              {emptyStateSubtitle}
            </p>
            {managerLiveChat && (
              <a
                href={wappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
              >
                <WhatsAppIcon className="h-4 w-4" />
                {locale === "es"
                  ? "Enviar mensaje por WhatsApp"
                  : "Send a message on WhatsApp"}
              </a>
            )}
          </div>
        )}
        {(() => {
          const reversedMessages = [...allMessages].reverse();
          const lastReadAtDate = effectiveLastReadAt
            ? new Date(effectiveLastReadAt)
            : null;

          // Find the index of the first unread message
          // Never show separator for manager contacts
          let firstUnreadIndex = -1;
          if (!isManagerContact) {
            if (lastReadAtDate) {
              // Find first message where sentAt > lastReadAt
              firstUnreadIndex = reversedMessages.findIndex(
                (msg) => new Date(msg.sentAt) > lastReadAtDate
              );
            } else if (
              effectiveLastReadAt === null &&
              reversedMessages.length > 0
            ) {
              // If lastReadAt is null, all messages are unread, separator at top
              firstUnreadIndex = 0;
            }
          }

          return reversedMessages.map((msg, index) => {
            // Show separator before the first unread message (never for managers)
            const showUnreadSeparator =
              !isManagerContact && index === firstUnreadIndex;

            const prevMsg = index > 0 ? reversedMessages[index - 1] : null;
            const showDateSeparator =
              !prevMsg ||
              formatChatDateSeparator(msg.sentAt, locale) !==
                formatChatDateSeparator(prevMsg.sentAt, locale);

            return (
              <React.Fragment key={msg.listKey}>
                {showDateSeparator && (
                  <div className="flex justify-center py-2">
                    <span className="rounded-full border border-chat-border bg-white/90 px-3 py-0.5 text-[10px] font-medium uppercase tracking-wide text-stone-600 shadow-sm">
                      {formatChatDateSeparator(msg.sentAt, locale)}
                    </span>
                  </div>
                )}
                {showUnreadSeparator && (
                  <div className="flex items-center gap-2 my-1">
                    <div className="flex-1 border-t border-primary/30" />
                    <span className="text-xs font-medium text-primary px-2">
                      {locale === "es" ? "Mensajes no leídos" : "Unread messages"}
                    </span>
                    <div className="flex-1 border-t border-primary/30" />
                  </div>
                )}
                <MessageBalloon
                  message={msg}
                  highlighted={messageMatchesTarget(msg)}
                  messageRef={(el) => {
                    if (el) {
                      messageRefs.current.set(msg.listKey, el);
                    } else {
                      messageRefs.current.delete(msg.listKey);
                    }
                  }}
                  contactId={contactId}
                />
              </React.Fragment>
            );
          });
        })()}
      </div>

      {/* Message input - hidden for manager contacts in live mode */}
      {!managerLiveChat ? (
        <div className="shrink-0 space-y-1">
          <div className="relative rounded-xl border border-stone-200 bg-white px-2 pb-1.5 pt-3 shadow-sm">
            <button
              type="button"
              aria-label={
                locale === "es"
                  ? "Arrastrar hacia arriba para agrandar"
                  : "Drag up to resize"
              }
              className="absolute left-1/2 top-0 z-10 flex h-4 w-12 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize touch-none items-center justify-center rounded-full border border-stone-200 bg-white text-stone-400 shadow-sm transition-colors hover:bg-stone-50 hover:text-stone-600"
              onPointerDown={handleComposerResizePointerDown}
              onPointerMove={handleComposerResizePointerMove}
              onPointerUp={handleComposerResizePointerEnd}
              onPointerCancel={handleComposerResizePointerEnd}
            >
              <GripHorizontal className="h-3 w-3" aria-hidden />
            </button>
            <div className="flex w-full items-center gap-1 lg:gap-2">
              <textarea
                value={messageToSend}
                onChange={(e) => setMessageToSend(e.target.value)}
                placeholder={composerPlaceholder}
                style={{ height: composerHeight }}
                className="flex-1 resize-none overflow-y-auto border-0 bg-transparent px-2 py-1.5 text-base leading-snug lg:text-sm focus:outline-none focus:ring-0"
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (e.metaKey || e.shiftKey) {
                      return;
                    }
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={uploading}
              />
              <label className="cursor-pointer flex shrink-0 items-center rounded-lg p-2 text-gray-500 hover:bg-stone-100">
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                <Paperclip className="h-5 w-5" aria-hidden />
                <span className="sr-only">
                  {locale === "es" ? "Adjuntar archivo" : "Attach file"}
                </span>
              </label>
              <button
                type="button"
                onClick={handleSend}
                disabled={!canSendMessage}
                className={`flex shrink-0 items-center justify-center rounded-full p-1 transition-all ${
                  canSendMessage || uploading
                    ? "text-primary hover:scale-105 hover:text-primary-light active:scale-95"
                    : "cursor-not-allowed text-stone-300"
                }`}
                aria-label={
                  uploading
                    ? locale === "es"
                      ? "Enviando mensaje"
                      : "Sending message"
                    : locale === "es"
                      ? "Enviar mensaje"
                      : "Send message"
                }
                title={
                  uploading
                    ? locale === "es"
                      ? "Enviando..."
                      : "Sending..."
                    : locale === "es"
                      ? "Enviar"
                      : "Send"
                }
              >
                {uploading ? (
                  <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
                ) : (
                  <Send
                    className={`h-8 w-8 ${canSendMessage ? "fill-primary/25" : ""}`}
                    strokeWidth={canSendMessage ? 2.5 : 2}
                    aria-hidden
                  />
                )}
              </button>
            </div>
            <p className="hidden px-2 pb-0 pt-0.5 text-[10px] text-gray-400 lg:block">
              {locale === "es"
                ? "Enter envía · Shift+Enter nueva línea"
                : "Enter to send · Shift+Enter for new line"}
            </p>
          </div>
          {selectedFile && (
            <div className="flex items-center gap-2 mt-1 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs">
              <Paperclip className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
              <span className="truncate flex-1 font-medium text-gray-700">
                {selectedFile.name}
              </span>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="shrink-0 rounded p-0.5 hover:bg-stone-100"
                aria-label={locale === "es" ? "Quitar archivo" : "Remove file"}
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            </div>
          )}
          {fileError && (
            <div className="text-xs text-red-500">{fileError}</div>
          )}
        </div>
      ) : (
        allMessages.length > 0 && whatsAppFooter
      )}
    </div>
  );
}
